import api from '../../utils/api';

export type ServiceType =
  | 'all'
  | 'AMBULANCE'
  | 'DOCTOR'
  | 'MEDICINE'
  | 'BLOOD_BANK'
  | 'LAB_TEST'
  | 'HOSPITAL'
  | 'PRODUCT';

export const SERVICE_TYPE_OPTIONS: { value: ServiceType; label: string }[] = [
  { value: 'all', label: 'Overall (All Services)' },
  { value: 'AMBULANCE', label: 'Ambulance Agency' },
  { value: 'DOCTOR', label: 'Doctor / Clinic' },
  // { value: 'MEDICINE',   label: 'Pharmacy / Medical Store' },
  // { value: 'BLOOD_BANK', label: 'Blood Bank' },
  // { value: 'LAB_TEST',   label: 'Diagnostic Center' },
  // { value: 'HOSPITAL',   label: 'Hospital (Bed Booking)' },
  // { value: 'PRODUCT',    label: 'Product Partner' },
];

export interface DailyDashboardOverview {
  total_revenue: number;
  total_orders: number;
  total_gmv: number;
  avg_active_users: number;
  latest_total_users: number;
  net_platform_revenue: number;
  platform_commission?: number;
  gst_on_commission?: number;
  gateway_fees?: number;
  total_refunded?: number;
  vendor_payout?: number;
}

export interface DailyDashboardTrendRow {
  date: string;
  total_revenue: number;
  total_orders: number;
  total_gmv?: number;
  active_users: number;
  net_platform_revenue: number;
  platform_commission?: number;
  gst_on_commission?: number;
  total_refunded?: number;
  vendor_payout?: number;
}

export interface EventUsageRow {
  event_type: string;
  name: string;
  total_count: number;
  unique_users: number;
  avg_time_spent_sec: number;
}

export interface FunnelRow {
  funnel_name: string;
  step_name: string;
  step_order: number;
  users_count: number;
  conversion_rate: number;
  drop_off_rate: number;
}

export interface ItemMetricRow {
  target_type: string;
  target_id: string;
  target_name?: string;
  service: string;
  views: number;
  clicks: number;
  searches: number;
  conversions: number;
  unique_users: number;
  avg_dwell_time_sec: number;
}

export interface StorageMetricRow {
  category: string;
  total_files: number;
  total_size_bytes: number;
  avg_file_size_bytes: number;
  active_users_count: number;
  total_uploads: number;
  total_deletions: number;
  net_storage_bytes: number;
  total_storage_gb: number;
}

export interface VendorMetricRow {
  vendor_id: string;
  vendor_name?: string;
  vendor_type: string;
  total_orders_received: number;
  orders_accepted: number;
  orders_rejected: number;
  acceptance_rate: number;
  completed_orders: number;
  avg_response_time_sec: number;
}

export interface BlogReaderDetail {
  user_id: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  blog_id: string;
  blog_title: string;
  blog_category: string;
  last_read_time: string;
  read_count: number;
  avg_dwell_sec?: number;
  total_dwell_sec?: number;
}

export interface AwarenessViewDetail {
  user_id: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  event_id: string;
  event_title: string;
  last_view_time: string;
  view_count: number;
  avg_dwell_sec?: number;
  total_dwell_sec?: number;
}

export interface BlogLookupDetail {
  id: string | number;
  title: string;
  categoryId?: number;
  category_name?: string;
}

export interface AwarenessLookupDetail {
  id: string;
  title: string;
  event_type: 'health' | 'seasonal';
}

export interface PaymentStatsDetail {
  count: number;
  amount: number;
}

export interface PaymentBreakdownInfo {
  details: {
    paid: PaymentStatsDetail;
    failed: PaymentStatsDetail;
    created: PaymentStatsDetail;
  };
  metrics: {
    successRate: number;
    abandonmentRate: number;
    lostRevenue: number;
  };
}

export interface ServiceOrderStats {
  count: number;
  gmv: number;
  statuses?: Record<string, number>;
}

export interface OrdersBreakdownInfo {
  AMBULANCE: ServiceOrderStats;
  DOCTOR: ServiceOrderStats;
  MEDICINE: ServiceOrderStats;
  LAB_TEST: ServiceOrderStats;
  BLOOD_BANK: ServiceOrderStats;
  HOSPITAL: ServiceOrderStats;
  PRODUCT: ServiceOrderStats;
}

export interface DailyAnalyticsDashboardData {
  from: string;
  to: string;
  serviceType?: ServiceType;
  overview: DailyDashboardOverview;
  paymentBreakdown?: PaymentBreakdownInfo;
  ordersBreakdown?: OrdersBreakdownInfo;
  netRevenueBreakdown?: Record<string, { orders: number; gmv: number; revenue: number; commission: number; gst_on_commission: number; gateway_fees: number; net_profit: number; vendor_payout: number; total_refunded?: number }>;
  trends: DailyDashboardTrendRow[];
  event_usage: EventUsageRow[];
  funnel: FunnelRow[];
  items: ItemMetricRow[];
  storage: StorageMetricRow[];
  vendors: VendorMetricRow[];
  readers?: BlogReaderDetail[];
  awareness?: AwarenessViewDetail[];
  allBlogs?: BlogLookupDetail[];
  allAwareness?: AwarenessLookupDetail[];
}

interface DashboardResponse {
  success: boolean;
  data: DailyAnalyticsDashboardData;
}

function toDateOnly(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export async function fetchDailyAnalyticsDashboard(
  from: Date,
  to: Date,
  serviceType: ServiceType = 'all',
  sections: 'all' | 'overview' | 'intelligence' | 'engagement' = 'all',
): Promise<DailyAnalyticsDashboardData> {
  const response = await api.get<DashboardResponse>('/master-admin/analytics/daily-dashboard', {
    params: {
      from: toDateOnly(from),
      to: toDateOnly(to),
      serviceType,
      sections,
    },
  });
  return response.data.data;
}

export type ActivityChannel = 'all' | 'web' | 'user_app' | 'plus_app';
export type ActivityVisitorType = 'all' | 'registered' | 'anonymous' | 'unknown';

export interface ActivityTrackingSummary {
  total_events: number;
  unique_visitors: number;
  unique_anonymous: number;
  unique_unknown?: number;
  unique_registered: number;
  sessions: number;
  avg_dwell_sec: number;
}

export interface ActivityChannelRow {
  channel: Exclude<ActivityChannel, 'all'>;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
}

export interface ActivityTrendRow {
  date: string;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
}

export interface ActivityPageRow {
  page: string;
  label: string;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
  avg_dwell_sec: number;
}

export interface ActivityEventRow {
  event_type: string;
  label: string;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
}

export interface ActivityTargetRow {
  target_type: string;
  target_id: string;
  label: string;
  events: number;
  unique_visitors: number;
}

export interface ActivityProductChild {
  key: string;
  group: string;
  label: string;
  description: string;
  parent: string | null;
  events: number;
  unique_visitors: number;
  unique_anonymous: number;
  avg_dwell_sec: number;
  visitor_share: number;
}

export interface ActivityProductRow extends ActivityProductChild {
  children: ActivityProductChild[];
}

export interface ActivityServiceBreakdown {
  services: ActivityProductRow[];
  features: ActivityProductRow[];
  other: ActivityProductChild;
}

export interface ActivityVisitorRow {
  visitor_id: string;
  visitor_type: 'registered' | 'anonymous' | string;
  channel: string;
  events: number;
  sessions?: number;
  last_seen: string;
  last_page: string;
  last_page_label: string;
  last_event: string;
  last_event_label: string;
  pages?: number;
}

export interface ActivityFilterOption {
  value: string;
  label: string;
}

export interface ActivityTrackingData {
  from: string;
  to: string;
  filters: {
    channel: ActivityChannel;
    visitorType: ActivityVisitorType;
    page: string;
    eventType: string;
    targetId: string;
    product?: string;
  };
  summary: ActivityTrackingSummary;
  by_channel: ActivityChannelRow[];
  trend: ActivityTrendRow[];
  by_page: ActivityPageRow[];
  by_event: ActivityEventRow[];
  by_target: ActivityTargetRow[];
  by_service?: ActivityServiceBreakdown;
  recent_visitors: ActivityVisitorRow[];
  unknown_visitors?: ActivityVisitorRow[];
  unknown_pages?: ActivityPageRow[];
  options: {
    pages: ActivityFilterOption[];
    events: ActivityFilterOption[];
    targets: ActivityFilterOption[];
  };
}

interface ActivityTrackingResponse {
  success: boolean;
  data: ActivityTrackingData;
}

export interface VisitorActivityEvent {
  timestamp: string;
  session_id: string;
  event_type: string;
  event_label: string;
  page: string;
  page_label: string;
  channel: string;
  visitor_type: string;
  dwell_time_sec: number;
}

export interface VisitorActivitySession {
  session_id: string;
  started_at: string;
  ended_at: string;
  channel: string;
  events: number;
  pages: number;
  activities: VisitorActivityEvent[];
}

export interface VisitorActivityData {
  visitor_id: string;
  visitor_type: string;
  from: string;
  to: string;
  total_sessions?: number;
  total_events?: number;
  sessions: VisitorActivitySession[];
}

interface VisitorActivityResponse {
  success: boolean;
  data: VisitorActivityData;
}

export async function fetchVisitorActivity(params: {
  visitorId: string;
  from: Date;
  to: Date;
}): Promise<VisitorActivityData> {
  const response = await api.get<VisitorActivityResponse>('/master-admin/analytics/activity-tracking/visitor', {
    params: {
      visitorId: params.visitorId,
      from: toDateOnly(params.from),
      to: toDateOnly(params.to),
    },
  });
  return response.data.data;
}

export async function fetchActivityTracking(params: {
  from: Date;
  to: Date;
  channel?: ActivityChannel;
  visitorType?: ActivityVisitorType;
  page?: string;
  eventType?: string;
  targetId?: string;
  product?: string;
}): Promise<ActivityTrackingData> {
  const response = await api.get<ActivityTrackingResponse>('/master-admin/analytics/activity-tracking', {
    params: {
      from: toDateOnly(params.from),
      to: toDateOnly(params.to),
      channel: params.channel || 'all',
      visitorType: params.visitorType || 'all',
      page: params.page && params.page !== 'all' ? params.page : undefined,
      eventType: params.eventType && params.eventType !== 'all' ? params.eventType : undefined,
      targetId: params.targetId && params.targetId !== 'all' ? params.targetId : undefined,
      product: params.product && params.product !== 'all' ? params.product : undefined,
    },
  });
  return response.data.data;
}

export interface DropoffUserRow {
  userId: string;
  name: string | null;
  phone_number: string;
  emailId: string | null;
  createdAt: string;
}

interface DropoffsResponse {
  success: boolean;
  data: DropoffUserRow[];
}

export async function fetchFunnelDropoffs(
  funnelName: string,
  stepOrder: number,
  from: Date,
  to: Date,
): Promise<DropoffUserRow[]> {
  const response = await api.get<DropoffsResponse>('/master-admin/analytics/funnel-dropoffs', {
    params: {
      funnelName,
      stepOrder,
      from: toDateOnly(from),
      to: toDateOnly(to),
    },
  });
  return response.data.data;
}

